import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ContactSection from "../components/ContactSection";

const DiversityInclusion = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const pillars = [
        {
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#2563eb" strokeWidth="1.8" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /></svg>
            ),
            title: 'Driving Innovation Through Diverse Perspectives',
            desc: 'Diversity goes beyond representation - it strengthens how we think, collaborate, and innovate. By bringing together people with varied experiences and viewpoints, we build stronger solutions and make better decisions for our clients and communities.'
        },
        {
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /><circle cx="9" cy="7" r="4" stroke="#2563eb" strokeWidth="1.8" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" /></svg>
            ),
            title: 'Building an Inclusive Environment',
            desc: 'Inclusion at Solaris means creating a space where everyone feels safe to contribute, collaborate, and grow. We encourage open communication, mutual respect, and shared accountability across all levels of the organisation - from leadership to every individual team member.'
        }
    ];

    const timeline = [
        {
            title: 'Inclusive Hiring',
            desc: 'We follow fair and transparent recruitment practices that encourage applications from diverse backgrounds, experiences, and perspectives - removing bias from every stage of the hiring process.',
            image: require('../assets/images/iclusive.avif'),
            imageLeft: false
        },
        {
            title: 'Continuous Learning',
            desc: 'We invest in ongoing training, leadership development, and awareness programmes that equip our teams with the knowledge and tools to champion inclusive behaviours in their daily work.',
            image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&auto=format&fit=crop&q=80',
            imageLeft: true
        },
        {
            title: 'Employee Resource Groups (ERGs)',
            desc: 'We support employee-led communities that promote connection, knowledge sharing, and peer support - giving every individual a platform to contribute to our culture and shape the Solaris experience.',
            image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&auto=format&fit=crop&q=80',
            imageLeft: false
        },
        {
            title: 'Community Engagement',
            desc: 'We partner with organisations and initiatives that promote diversity, equity, and social responsibility - extending our commitment to inclusion beyond our own walls into the communities we serve.',
            image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&auto=format&fit=crop&q=80',
            imageLeft: true
        }
    ];

    const stats = [
        { value: '4+', label: 'Open forums conducted annually to promote inclusivity and leadership accessibility.', highlight: false },
        { value: '100%', label: 'Equal opportunity framework ensuring fair growth, promotions, and leadership access for all employees.', highlight: true },
        { value: '90%', label: 'Employee participation in Diversity & Inclusion training programs.', highlight: true },
        { value: '5+', label: 'Active ERGs fostering collaboration and cultural exchange across the organisation.', highlight: false },
    ];

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '520px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center top', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.76)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '140px 15px 100px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '28px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Our Culture
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                            lineHeight: 1.15, margin: '0 auto 24px', maxWidth: '820px'
                        }}>
                            Built on Respect<br />and Belonging
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.72)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '540px', margin: '0 auto 40px'
                        }}>
                            At Solaris, diversity and inclusion are not policies - they are the principles that shape how we hire, lead, collaborate, and grow together every single day.
                        </p>
                        <Link to="/contact" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                color: '#fff', border: 'none',
                                padding: '14px 8px 14px 28px',
                                borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                cursor: 'pointer', boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                            >
                                <span style={{ marginRight: '16px' }}>Learn More</span>
                                <span style={{
                                    background: '#fff', borderRadius: '50%', width: '38px', height: '38px',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ── WHY IT MATTERS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5" style={{ marginBottom: '72px' }}>
                        <div className="col-lg-6">
                            <p style={{
                                color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>WHY IT MATTERS</p>
                            <h2 style={{
                                fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800,
                                color: '#0f172a', lineHeight: 1.2, marginBottom: '24px'
                            }}>
                                Built on Respect and Belonging
                            </h2>
                            <div style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                <img
                                    src={require('../assets/images/Built.jpg')}
                                    alt="Diverse team"
                                    style={{ width: '100%', height: '340px', objectFit: 'cover', display: 'block' }}
                                />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div style={{
                                background: '#f8fafc', borderRadius: '20px',
                                padding: '48px 40px', border: '1px solid #e2e8f0'
                            }}>
                                <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: 0 }}>
                                    We believe that innovation thrives when people feel respected, empowered, and heard. We foster an environment where individuals from different backgrounds, experiences, and perspectives come together to create meaningful impact - for our clients, our teams, and the wider communities we serve.
                                </p>
                            </div>

                            <div className="row g-3 mt-2">
                                {pillars.map((p, i) => (
                                    <div key={i} className="col-12">
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.5, delay: i * 0.12 }}
                                            viewport={{ once: true }}
                                            style={{
                                                display: 'flex', gap: '18px', alignItems: 'flex-start',
                                                padding: '24px', borderRadius: '14px',
                                                border: '1px solid #e2e8f0', background: '#fff',
                                                transition: 'box-shadow 0.2s, border-color 0.2s'
                                            }}
                                            whileHover={{ boxShadow: '0 8px 32px rgba(37,99,235,0.1)', borderColor: '#bfdbfe' }}
                                        >
                                            <div style={{
                                                width: '46px', height: '46px', borderRadius: '10px',
                                                background: '#eff6ff', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                            }}>
                                                {p.icon}
                                            </div>
                                            <div>
                                                <h4 style={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>{p.title}</h4>
                                                <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: 0 }}>{p.desc}</p>
                                            </div>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── HOW WE FOSTER - TIMELINE ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>HOW WE FOSTER</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            How We Foster{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Diversity &amp; Inclusion
                            </span>
                        </h2>
                    </div>

                    {/* Timeline */}
                    <div style={{ position: 'relative' }}>
                        {/* Centre line - desktop only */}
                        <div style={{
                            position: 'absolute', left: '50%', top: 0, bottom: 0,
                            width: '2px', background: 'linear-gradient(to bottom, #dbeafe, #bfdbfe, #dbeafe)',
                            transform: 'translateX(-50%)', pointerEvents: 'none'
                        }} className="d-none d-lg-block" />

                        {timeline.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 32 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                                className="row align-items-center g-0"
                                style={{ marginBottom: '64px', position: 'relative' }}
                            >
                                {/* Dot on line */}
                                <div style={{
                                    position: 'absolute', left: '50%', top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '20px', height: '20px', borderRadius: '50%',
                                    background: '#fff', border: '3px solid #2563eb',
                                    zIndex: 2, boxShadow: '0 0 0 4px #dbeafe'
                                }} className="d-none d-lg-block" />

                                {/* Text side */}
                                <div className={`col-lg-5 ${item.imageLeft ? 'order-lg-2' : 'order-lg-1'}`}
                                    style={{ padding: item.imageLeft ? '0 0 0 56px' : '0 56px 0 0' }}>
                                    <div style={{
                                        background: '#fff', borderRadius: '16px',
                                        padding: '36px 32px', border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 24px rgba(0,0,0,0.05)'
                                    }}>
                                        <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: '14px' }}>{item.title}</h3>
                                        <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: 0 }}>{item.desc}</p>
                                    </div>
                                </div>

                                {/* Spacer for dot */}
                                <div className="col-lg-2 d-none d-lg-block" />

                                {/* Image side */}
                                <div className={`col-lg-5 ${item.imageLeft ? 'order-lg-1' : 'order-lg-2'} mt-4 mt-lg-0`}
                                    style={{ padding: item.imageLeft ? '0 56px 0 0' : '0 0 0 56px' }}>
                                    <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── METRICS - DARK ── */}
            <section style={{ padding: '100px 0', background: '#0f172a', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 10% 50%, rgba(37,99,235,0.12) 0%, transparent 55%), radial-gradient(ellipse at 90% 20%, rgba(14,165,233,0.1) 0%, transparent 55%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        {/* Left */}
                        <div className="col-lg-4">
                            <motion.div
                                initial={{ opacity: 0, x: -24 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                                    borderRadius: '50px', padding: '5px 16px', marginBottom: '24px'
                                }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                                    <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                        Progress Rooted in Purpose
                                    </span>
                                </div>
                                <h2 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                                    lineHeight: 1.2, marginBottom: '28px'
                                }}>
                                    Transforming<br />Commitment<br />into Action
                                </h2>
                                <div style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                    <img
                                        src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600&auto=format&fit=crop&q=80"
                                        alt="Team collaboration"
                                        style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                                    />
                                </div>
                            </motion.div>
                        </div>

                        {/* Right: stat grid */}
                        <div className="col-lg-8">
                            <div className="row g-3">
                                {stats.map((s, i) => (
                                    <div key={i} className="col-6">
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5, delay: i * 0.1 }}
                                            viewport={{ once: true }}
                                            style={{
                                                padding: '36px 28px', borderRadius: '16px',
                                                background: s.highlight
                                                    ? 'linear-gradient(135deg, #2563eb, #0ea5e9)'
                                                    : 'rgba(255,255,255,0.06)',
                                                border: s.highlight ? 'none' : '1px solid rgba(255,255,255,0.1)',
                                                height: '100%'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: 'clamp(2.2rem, 4vw, 3.2rem)',
                                                fontWeight: 800, color: '#fff',
                                                lineHeight: 1, marginBottom: '16px'
                                            }}>
                                                {s.value}
                                            </div>
                                            <p style={{
                                                color: s.highlight ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.55)',
                                                fontSize: '0.88rem', lineHeight: 1.7, margin: 0
                                            }}>
                                                {s.label}
                                            </p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default DiversityInclusion;
